import React, { useState, useEffect, useContext } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { ReactComponent as AgencyBriefcase } from '../../../assets/svgs/briefcase.svg'
import { ReactComponent as CompanyBuilding } from '../../../assets/svgs/building.svg'
import { ReactComponent as ArrowsMove } from '../../../assets/svgs/arrows-move.svg'
import { ReactComponent as ArrowRight } from '../../../assets/svgs/arrow-right-blue.svg'
import { ReactComponent as CloseIcon } from '../../../assets/svgs/x.svg'
import {
  BlurredBackground,
  StyledModalDialog,
} from '../../../components/modal/styled'
import { useTheme } from '../../../contexts/themeContext'
import apiCompanyUsers from '../../configuration/services/companyUser'
import { userContext } from '../../../contexts/userContext'
import { endSession } from '../../../services/auth/session'

const AGENCY_TYPE = 'AGY'
const CLIENT_TYPE = 'CLT'

const normalizeCompanyType = (type) => {
  if (!type) return null
  return ['AG', 'AGY', 'AGENCY'].includes(String(type).toUpperCase())
    ? AGENCY_TYPE
    : CLIENT_TYPE
}

const companyTypeFromIdentity = (user) =>
  user?.isAgency ? AGENCY_TYPE : CLIENT_TYPE

const useCompanyTransitionData = () => {
  const getCompanyFromStorage = (key) => {
    const companyData = localStorage.getItem(key)
    if (!companyData) return null
    const company = JSON.parse(companyData)
    return { ...company, type: normalizeCompanyType(company.type) }
  }

  const currentCompany = getCompanyFromStorage('currentCompany')
  const previousCompany = getCompanyFromStorage('previousCompany')

  const iconForType = (type) =>
    type === AGENCY_TYPE ? (
      <AgencyBriefcase width={26} height={26} />
    ) : (
      <CompanyBuilding width={26} height={26} />
    )

  return {
    currentCompany,
    previousCompany,
    currentIcon: iconForType(currentCompany?.type),
    previousIcon: iconForType(previousCompany?.type),
  }
}

function CompanyTransitionModal() {
  const { theme } = useTheme()
  const { t, i18n } = useTranslation()
  const isRTL = i18n?.language === 'ar'
  const { state: user } = useContext(userContext)
  const { currentCompany, previousCompany, currentIcon, previousIcon } =
    useCompanyTransitionData()

  const [modalDisplayType, setModalDisplayType] = useState('block')
  const [isInitialMount, setIsInitialMount] = useState(true)

  const handleLogout = () => {
    localStorage.clear()
    endSession().finally(() => {
      window.location.href = '/login'
    })
  }

  const handleClose = () => {
    setModalDisplayType('none')
    sessionStorage.removeItem('companyChanged')
  }

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false)
    }
  }, [])

  useEffect(() => {
    if (user?.companyId) {
      const storedCurrent = localStorage.getItem('currentCompany')
      const current = storedCurrent ? JSON.parse(storedCurrent) : null

      if (!current || current.id !== user.companyId) {
        if (current) {
          localStorage.setItem('previousCompany', JSON.stringify(current))
        }
        const newCompany = {
          id: user.companyId,
          type: companyTypeFromIdentity(user),
          name: user.companyName,
        }
        localStorage.setItem('currentCompany', JSON.stringify(newCompany))
      } else if (normalizeCompanyType(current.type) === null) {
        localStorage.setItem(
          'currentCompany',
          JSON.stringify({ ...current, type: companyTypeFromIdentity(user) }),
        )
      }
    }
  }, [user?.companyId])

  useEffect(() => {
    if (
      !currentCompany ||
      !previousCompany ||
      currentCompany.id === previousCompany.id ||
      isInitialMount ||
      !sessionStorage.getItem('companyChanged')
    ) {
      return undefined
    }

    let isMounted = true

    const checkAdminAndLogout = async () => {
      const isNotAdmin = await apiCompanyUsers.checkNotAdminUser()
      if (isMounted && !isNotAdmin) handleLogout()
    }

    checkAdminAndLogout()

    const timer = setTimeout(() => {
      if (isMounted) {
        setModalDisplayType('none')
        sessionStorage.removeItem('companyChanged')
      }
    }, 2000)

    return () => {
      isMounted = false
      clearTimeout(timer)
    }
  }, [currentCompany, previousCompany, isInitialMount])

  if (
    !currentCompany ||
    !previousCompany ||
    currentCompany.id === previousCompany.id ||
    isInitialMount ||
    !sessionStorage.getItem('companyChanged')
  ) {
    return null
  }

  const iconTile = (icon, accent) => (
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        backgroundColor: theme.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: accent,
        boxShadow: '0px 2px 6px rgba(18, 38, 63, 0.08)',
      }}
    >
      {icon}
    </div>
  )

  const companyCard = ({
    label,
    type,
    name,
    icon,
    accent,
    highlighted,
    showNew,
  }) => (
    <div
      style={{
        flex: 1,
        position: 'relative',
        borderRadius: 14,
        padding: '1.25rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.75rem',
        textAlign: 'center',
        border: `1.5px solid ${highlighted ? theme.cardBorder : theme.brightGray}`,
        backgroundColor: highlighted ? theme.water : theme.gray50,
      }}
    >
      {showNew && (
        <span
          style={{
            position: 'absolute',
            top: -11,
            [isRTL ? 'left' : 'right']: 14,
            backgroundColor: theme.darkCerulean,
            color: theme.white,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.05em',
            padding: '3px 10px',
            borderRadius: 999,
          }}
        >
          {t('agencyTypes.new')}
        </span>
      )}
      {iconTile(icon, accent)}
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.slateGray,
            marginBottom: 4,
          }}
        >
          {label}
          {type ? ` · ${type}` : ''}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.sidebar }}>
          {name}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <BlurredBackground modalDisplayType={modalDisplayType} />
      <StyledModalDialog
        modalDisplayType={modalDisplayType}
        style={{ width: 'min(520px, calc(100vw - 1rem))' }}
      >
        <Modal.Body style={{ padding: '1.75rem' }}>
          <button
            type="button"
            aria-label="close"
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: 16,
              [isRTL ? 'left' : 'right']: 16,
              width: 34,
              height: 34,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: theme.brightGray,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.slateGray,
              cursor: 'pointer',
            }}
          >
            <CloseIcon width={16} height={16} />
          </button>

          <div className="d-flex flex-column align-items-center text-center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: theme.brightGray,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.cardBorder,
                marginBottom: '1rem',
              }}
            >
              <ArrowsMove width={26} height={26} style={{ rotate: '45deg' }} />
            </div>
            <h5
              style={{ fontWeight: 700, color: theme.sidebar, marginBottom: 8 }}
            >
              {t('agencyTypes.switchOrganizationTitle')}
            </h5>
            <p
              style={{
                color: theme.slateGray,
                fontSize: 14,
                maxWidth: 360,
                margin: 0,
              }}
            >
              {t('agencyTypes.switchOrganizationSubtitle')}
            </p>
          </div>

          <div
            className="d-flex align-items-center flex-wrap flex-sm-nowrap justify-content-center"
            style={{ gap: '0.75rem', marginTop: '1.5rem' }}
          >
            {companyCard({
              label: t('agencyTypes.from'),
              type: previousCompany?.type,
              name: previousCompany?.name,
              icon: previousIcon,
              accent: theme.slateGray,
              highlighted: false,
              showNew: false,
            })}
            <ArrowRight
              width={22}
              height={22}
              style={{
                flexShrink: 0,
                color: theme.cardBorder,
                transform: isRTL ? 'scaleX(-1)' : 'none',
              }}
            />
            {companyCard({
              label: t('agencyTypes.to'),
              type: currentCompany?.type,
              name: currentCompany?.name,
              icon: currentIcon,
              accent: theme.cardBorder,
              highlighted: true,
              showNew: true,
            })}
          </div>
        </Modal.Body>
      </StyledModalDialog>
    </>
  )
}

export default React.memo(CompanyTransitionModal)
